import { useCallback, useEffect, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  preview: string;
  image_url?: string;
  facebook_url?: string;
  twitter_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  youtube_url?: string;
  tiktok_url?: string;
  threads_url?: string;
  google_business_url?: string;
  pinterest_url?: string;
  snapchat_url?: string;
  bluesky_url?: string;
  reddit_url?: string;
  substack_url?: string;
  discord_url?: string;
  social_media_script?: string;
  published: boolean;
  published_at?: string;
  created_at: string;
  user_id: string;
  post_tags?: {
    tag_id: string;
    tags: {
      id: string;
      name: string;
      slug: string;
    };
  }[];
}

interface UseBlogPostsOptions {
  authLoading: boolean;
  isAdmin: boolean;
  isReviewer: boolean;
}

export const useBlogPosts = ({ authLoading, isAdmin, isReviewer }: UseBlogPostsOptions) => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTagFilter, setSelectedTagFilter] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"latest" | "oldest">("latest");
  const [availableTags, setAvailableTags] = useState<Array<{ id: string; name: string; slug: string }>>([]);

  const fetchBlogPosts = useCallback(async () => {
    try {
      let filteredPostIds: string[] | null = null;

      if (selectedTagFilter) {
        const { data: tagData, error: tagError } = await supabase
          .from("tags")
          .select("id")
          .eq("slug", selectedTagFilter)
          .single();

        if (tagError || !tagData) {
          setBlogPosts([]);
          setLoading(false);
          return;
        }

        let postTagsQuery;

        if (!isAdmin && !isReviewer) {
          postTagsQuery = supabase
            .from("post_tags")
            .select("blog_post_id, blog_posts!inner(published)")
            .eq("tag_id", tagData.id)
            .eq("blog_posts.published", true);
        } else {
          postTagsQuery = supabase.from("post_tags").select("blog_post_id").eq("tag_id", tagData.id);
        }

        const { data: postTagsData, error: postTagsError } = await postTagsQuery;

        if (postTagsError) throw postTagsError;

        filteredPostIds = postTagsData?.map((pt: any) => pt.blog_post_id) || [];

        if (filteredPostIds.length === 0) {
          setBlogPosts([]);
          setLoading(false);
          return;
        }
      }

      let query = supabase.from("blog_posts").select(`
          id,
          title,
          content,
          preview,
          image_url,
          facebook_url,
          post_tags (
            tag_id,
            tags (
              id,
              name,
              slug
            )
          ),
          twitter_url,
          instagram_url,
          linkedin_url,
          youtube_url,
          tiktok_url,
          reddit_url,
          substack_url,
          discord_url,
          threads_url,
          google_business_url,
          pinterest_url,
          snapchat_url,
          bluesky_url,
          social_media_script,
          published,
          published_at,
          created_at,
          user_id
        `);

      if (!isAdmin && !isReviewer) {
        query = query.eq("published", true);
      }

      if (filteredPostIds !== null) {
        query = query.in("id", filteredPostIds);
      }

      const { data, error } = await query.order("published_at", {
        ascending: sortOrder === "oldest",
        nullsFirst: false,
      });

      if (error) throw error;
      setBlogPosts(data || []);
    } catch (error: any) {
      console.error("Error fetching blog posts:", error);
      toast.error(`Error loading posts: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isReviewer, selectedTagFilter, sortOrder]);

  useEffect(() => {
    const fetchAvailableTags = async () => {
      try {
        let query = supabase.from("post_tags").select(`
            tags (
              id,
              name,
              slug
            ),
            blog_posts!inner (
              published
            )
          `);

        if (!isAdmin && !isReviewer) {
          query = query.eq("blog_posts.published", true);
        }

        const { data, error } = await query;

        if (error) throw error;

        const tagMap = new Map();
        data?.forEach((item: any) => {
          if (item.tags) {
            tagMap.set(item.tags.id, {
              id: item.tags.id,
              name: item.tags.name,
              slug: item.tags.slug,
            });
          }
        });

        const uniqueTags = Array.from(tagMap.values()).sort((a, b) => a.name.localeCompare(b.name));

        setAvailableTags(uniqueTags);
      } catch (error: any) {
        console.error("Error fetching tags:", error);
      }
    };

    if (!authLoading) {
      fetchAvailableTags();
    }
  }, [authLoading, isAdmin, isReviewer]);

  useEffect(() => {
    if (!authLoading) {
      fetchBlogPosts();
    }
  }, [authLoading, fetchBlogPosts]);

  return {
    blogPosts,
    loading,
    availableTags,
    selectedTagFilter,
    setSelectedTagFilter,
    sortOrder,
    setSortOrder,
    fetchBlogPosts,
  };
};

export type { BlogPost };
